import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { useMutation, useLazyQuery } from "@apollo/client/react";
import {
  Loader2,
  Sparkles,
  Save,
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronRight,
  FileText,
  Plus,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  Trash2,
  Rocket,
} from "lucide-react";
import { toast } from "sonner";

import { GET_PROMPT_LAYERS, GET_PENDING_EXERCISE_PREVIEWS } from "@/graphql/queries/admin";

// Lithuanian noun pluralization: 1 → singular, 2-9 → plural, 0/10-19 → genitive plural
function ltPlural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} ${one}`;
  if (mod10 >= 2 && mod10 <= 9 && (mod100 < 12 || mod100 > 19)) return `${n} ${few}`;
  return `${n} ${many}`;
}
import {
  GENERATE_EXERCISE_FILES,
  SAVE_SKILL_PROMPT,
  SAVE_PROMPT_LAYER,
  APPROVE_EXERCISE_FILES,
  REJECT_EXERCISE_FILES,
} from "@/graphql/mutations/admin";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

// Radix Select cannot hold an empty string as a value, so "the whole skill" needs
// a sentinel. It never reaches the server — setConceptKey turns it back into "".
const ALL_CONCEPTS = "__all__";

interface LayerInfo {
  key: string;
  label: string;
  filename: string;
  path: string;
  exists: boolean;
  content: string | null;
}

interface ComposedSegment {
  type: "layer" | "shape_pack" | "concepts" | "skill";
  label: string;
  content: string;
}

/** One thing the skill's theory teaches, and its questions are tagged with. */
interface SkillConcept {
  key: string;
  name: string;
  description?: string | null;
}

interface LayersData {
  layers: LayerInfo[];
  composed_segments: ComposedSegment[];
  /** The skill's vocabulary. Empty for a skill that has never been tagged. */
  concepts?: SkillConcept[];
}

interface ExercisePreview {
  filePath: string;
  problemHtml: string | null;
  answer: string | null;
  choices: string[] | null;
  hint: string | null;
}

export default function ExerciseGeneratePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const skillKey = searchParams.get("skill") || "";
  const skillName = searchParams.get("name") || skillKey;
  const subject = searchParams.get("subject") || "math";
  const grade = parseInt(searchParams.get("grade") || "1");

  // Settings - derived from URL params
  const level = searchParams.get("level") || "1";
  const count = searchParams.get("count") || "50";
  const model = searchParams.get("model") || "default";
  // "" means the whole skill: every question tagged with whatever it teaches.
  const conceptKey = searchParams.get("concept") || "";

  const updateParam = useCallback(
    (key: string, value: string, defaultValue: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === defaultValue) {
            next.delete(key);
          } else {
            next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setLevel = useCallback((v: string) => updateParam("level", v, "1"), [updateParam]);
  const setCount = useCallback((v: string) => updateParam("count", v, "50"), [updateParam]);
  const setModel = useCallback((v: string) => updateParam("model", v, "default"), [updateParam]);
  const setConceptKey = useCallback(
    (v: string) => updateParam("concept", v === ALL_CONCEPTS ? "" : v, ""),
    [updateParam],
  );

  // Layers
  const [layersData, setLayersData] = useState<LayersData | null>(null);
  const [layersLoading, setLayersLoading] = useState(false);
  const [expandedLayers, setExpandedLayers] = useState<Set<string>>(new Set());
  const [editedLayers, setEditedLayers] = useState<Record<string, string>>({});
  const [showComposed, setShowComposed] = useState(false);

  // Generated exercises
  const [exercises, setExercises] = useState<ExercisePreview[]>([]);
  const [generating, setGenerating] = useState(false);

  // Per-exercise approval tracking
  const [approvedPaths, setApprovedPaths] = useState<Set<string>>(new Set());

  // Loading states
  const [approving, setApproving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Step tracking
  type Step = "prompt" | "review";
  const [currentStep, setCurrentStep] = useState<Step>("prompt");

  // Draft exercises count (for showing "resume" banner)
  const [draftCount, setDraftCount] = useState<number | null>(null);
  const [loadingDrafts, setLoadingDrafts] = useState(false);

  const [fetchLayers] = useLazyQuery(GET_PROMPT_LAYERS, {
    fetchPolicy: "network-only",
  });
  const [fetchPendingPreviews] = useLazyQuery(GET_PENDING_EXERCISE_PREVIEWS, {
    fetchPolicy: "network-only",
  });
  const [saveSkillPrompt, { loading: savingSkill }] = useMutation(SAVE_SKILL_PROMPT);
  const [savePromptLayer, { loading: savingLayer }] = useMutation(SAVE_PROMPT_LAYER);
  const [generateExerciseFiles] = useMutation(GENERATE_EXERCISE_FILES);
  const [approveExerciseFiles] = useMutation(APPROVE_EXERCISE_FILES);
  const [rejectExerciseFiles] = useMutation(REJECT_EXERCISE_FILES);

  const concepts: SkillConcept[] = layersData?.concepts || [];

  const loadLayers = useCallback(async () => {
    if (!skillKey || !subject) return;
    setLayersLoading(true);
    try {
      const vars: Record<string, string | number> = {
        subject,
        grade,
        level: parseInt(level),
        skillKey,
        count: parseInt(count) || 50,
        ...(conceptKey ? { conceptKey } : {}),
      };
      const { data } = await fetchLayers({ variables: vars });
      const result = data?.promptLayers
        ? typeof data.promptLayers === "string"
          ? JSON.parse(data.promptLayers)
          : data.promptLayers
        : null;
      setLayersData(result);
      setEditedLayers({});
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      toast.error("Nepavyko užkrauti prompt sluoksnių");
    } finally {
      setLayersLoading(false);
    }
  }, [skillKey, subject, grade, level, count, conceptKey, fetchLayers]);

  useEffect(() => {
    loadLayers();
  }, [loadLayers]);

  // Check for draft exercises on mount (and when skill/level changes)
  const checkDrafts = useCallback(async () => {
    if (!skillKey) return;
    try {
      const { data } = await fetchPendingPreviews({
        variables: { skillKey, level: parseInt(level) },
      });
      const drafts = data?.pendingExercisePreviews || [];
      setDraftCount(drafts.length);
    } catch {
      setDraftCount(null);
    }
  }, [skillKey, level, fetchPendingPreviews]);

  useEffect(() => {
    // Only check for drafts if we don't already have exercises loaded
    if (exercises.length === 0) {
      checkDrafts();
    }
  }, [checkDrafts, exercises.length]);

  const handleLoadDrafts = async () => {
    setLoadingDrafts(true);
    try {
      const { data } = await fetchPendingPreviews({
        variables: { skillKey, level: parseInt(level) },
      });
      const drafts = data?.pendingExercisePreviews || [];
      if (drafts.length > 0) {
        setExercises(drafts);
        setApprovedPaths(new Set());
        setCurrentStep("review");
        setDraftCount(null);
        toast.success(`Užkrauta ${ltPlural(drafts.length, "neperžiūrėtas pratimas", "neperžiūrėti pratimai", "neperžiūrėtų pratimų")}`);
      } else {
        toast.info("Nėra neperžiūrėtų pratimų");
        setDraftCount(0);
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Klaida kraunant pratimus");
    } finally {
      setLoadingDrafts(false);
    }
  };

  const toggleLayer = (key: string) => {
    setExpandedLayers((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const setLayerContent = (key: string, content: string) => {
    setEditedLayers((prev) => ({ ...prev, [key]: content }));
  };

  const isLayerModified = (layer: LayerInfo) => {
    return layer.key in editedLayers && editedLayers[layer.key] !== (layer.content || "");
  };

  const handleSaveLayer = async (layer: LayerInfo) => {
    const content = editedLayers[layer.key] ?? layer.content ?? "";

    if (layer.key === "skill") {
      try {
        const { data } = await saveSkillPrompt({
          variables: { skillKey, subject, grade, content },
        });
        if (data?.saveSkillPrompt?.success) {
          toast.success("Išsaugota");
          await loadLayers();
        } else {
          toast.error(data?.saveSkillPrompt?.error || "Klaida");
        }
      } catch (e: unknown) {
        toast.error(e instanceof Error ? e.message : "Klaida");
      }
    } else {
      const vars: Record<string, string | number> = {
        layerKey: layer.key,
        content,
        subject,
        grade,
      };
      if (level) vars.level = parseInt(level);

      try {
        const { data } = await savePromptLayer({ variables: vars });
        if (data?.savePromptLayer?.success) {
          toast.success("Išsaugota");
          await loadLayers();
        } else {
          toast.error(data?.savePromptLayer?.error || "Klaida");
        }
      } catch (e: unknown) {
        toast.error(e instanceof Error ? e.message : "Klaida");
      }
    }
  };

  const anyModified = layersData?.layers.some((l) => isLayerModified(l)) || false;

  const getPromptOverride = (): string | undefined => {
    if (!anyModified) return undefined;
    const skillLayer = layersData?.layers.find((l) => l.key === "skill");
    if (skillLayer && isLayerModified(skillLayer)) {
      return editedLayers["skill"];
    }
    return undefined;
  };

  const handleGenerate = async () => {
    const parsedCount = parseInt(count) || 50;
    setGenerating(true);
    setExercises([]);
    setApprovedPaths(new Set());
    try {
      const promptOverride = getPromptOverride();
      const { data } = await generateExerciseFiles({
        variables: {
          skillKey,
          subject,
          grade,
          level: parseInt(level),
          count: parsedCount,
          model,
          ...(conceptKey ? { conceptKey } : {}),
          ...(promptOverride ? { promptOverride } : {}),
        },
      });
      const result = data?.generateExerciseFiles;
      if (result?.success) {
        toast.success(`Sugeneruota ${ltPlural(result.successCount, "pratimas", "pratimai", "pratimų")}`);
        const params = new URLSearchParams({
          view: "pending",
          skill: skillKey,
          level,
          subject,
          grade: String(grade),
        });
        navigate(`/admin/exercises?${params}`);
        return;
      } else {
        toast.error(result?.errors?.join(", ") || "Generavimo klaida");
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Klaida");
    } finally {
      setGenerating(false);
    }
  };

  const toggleApproval = (filePath: string) => {
    setApprovedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(filePath)) next.delete(filePath);
      else next.add(filePath);
      return next;
    });
  };

  const handleApproveAll = () => {
    setApprovedPaths(new Set(exercises.map((e) => e.filePath)));
  };

  const handleUnapproveAll = () => {
    setApprovedPaths(new Set());
  };

  const handleSaveApproved = async () => {
    if (approvedPaths.size === 0) {
      toast.error("Nepasirinktas nė vienas pratimas");
      return;
    }

    setApproving(true);
    try {
      const paths = Array.from(approvedPaths);
      const { data } = await approveExerciseFiles({ variables: { filePaths: paths } });
      if (data?.approveExerciseFiles?.success) {
        toast.success(`Patvirtinta ${ltPlural(data.approveExerciseFiles.approvedCount, "pratimas", "pratimai", "pratimų")}`);
        // Remove approved exercises from the list, keep only unapproved
        setExercises((prev) => prev.filter((e) => !approvedPaths.has(e.filePath)));
        setApprovedPaths(new Set());
      } else {
        toast.error(data?.approveExerciseFiles?.errors?.join(", ") || "Klaida");
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Klaida");
    } finally {
      setApproving(false);
    }
  };

  const handleClearUnapproved = async () => {
    const unapprovedExercises = exercises.filter((e) => !approvedPaths.has(e.filePath));
    if (unapprovedExercises.length === 0) {
      toast.info("Visi pratimai patvirtinti");
      return;
    }

    setDeleting(true);
    try {
      const paths = unapprovedExercises.map((e) => e.filePath);
      const { data } = await rejectExerciseFiles({ variables: { filePaths: paths } });
      if (data?.rejectExerciseFiles?.success) {
        // Remove rejected exercises from the list
        setExercises((prev) => prev.filter((e) => approvedPaths.has(e.filePath)));
        toast.success(`Ištrinta ${ltPlural(data.rejectExerciseFiles.rejectedCount, "pratimas", "pratimai", "pratimų")}`);
      } else {
        toast.error(data?.rejectExerciseFiles?.errors?.join(", ") || "Klaida");
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Klaida");
    } finally {
      setDeleting(false);
    }
  };

  const backUrl = `/admin/exercises?subject=${subject}&grade=${grade}`;

  if (!skillKey) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        <p>Nenurodytas įgūdis.</p>
        <Link to="/admin/exercises" className="text-primary underline mt-2 inline-block">
          Grįžti į pratimų sąrašą
        </Link>
      </div>
    );
  }

  const layers = layersData?.layers || [];
  const approvedCount = approvedPaths.size;
  const unapprovedCount = exercises.length - approvedCount;

  return (
    <div className="space-y-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to={backUrl}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Atgal
          </Link>
        </Button>
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            Generuoti pratimus
          </h1>
          <p className="text-sm text-muted-foreground">
            {skillName} <span className="text-xs">({skillKey})</span>
          </p>
        </div>
      </div>

      {/* Step indicators */}
      <div className="flex items-center gap-2 text-sm">
        <StepBadge step={1} label="Prompt" active={currentStep === "prompt"} done={currentStep === "review"} onClick={() => setCurrentStep("prompt")} />
        <span className="text-muted-foreground">&rarr;</span>
        <StepBadge step={2} label="Peržiūra" active={currentStep === "review"} done={false} onClick={() => exercises.length > 0 ? setCurrentStep("review") : undefined} />
      </div>

      {/* Step 1: Prompt & Settings */}
      {currentStep === "prompt" && (
        <div className="space-y-4">
          {/* Draft exercises banner */}
          {draftCount != null && draftCount > 0 && exercises.length === 0 && (
            <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
                      Yra {ltPlural(draftCount!, "neperžiūrėtas pratimas", "neperžiūrėti pratimai", "neperžiūrėtų pratimų")} (lygis {level})
                    </span>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleLoadDrafts}
                    disabled={loadingDrafts}
                    className="border-amber-400 text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900"
                  >
                    {loadingDrafts ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                    ) : (
                      <Eye className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    Peržiūrėti
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Settings row */}
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className={`grid gap-4 ${concepts.length > 0 ? "grid-cols-4" : "grid-cols-3"}`}>
                <div className="space-y-2">
                  <Label>Lygis</Label>
                  <Select value={level} onValueChange={setLevel}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 - Lengvas</SelectItem>
                      <SelectItem value="2">2 - Vidutinis</SelectItem>
                      <SelectItem value="3">3 - Sunkus</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Aim the whole batch at one thing the theory teaches. Without
                    this the only way to fill a single concept's hole was the "+"
                    in the exercise browser, which gives no prompt preview. */}
                {concepts.length > 0 && (
                  <div className="space-y-2">
                    <Label>Sąvoka</Label>
                    <Select
                      value={conceptKey || ALL_CONCEPTS}
                      onValueChange={setConceptKey}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ALL_CONCEPTS}>Visos temos sąvokos</SelectItem>
                        {concepts.map((concept) => (
                          <SelectItem key={concept.key} value={concept.key}>
                            {concept.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Kiekis</Label>
                  <Input
                    type="number"
                    min={1}
                    max={100}
                    value={count}
                    onChange={(e) => setCount(e.target.value)}
                    placeholder="5"
                  />
                </div>

                <div className="space-y-2">
                  <Label>AI modelis</Label>
                  <Select value={model} onValueChange={setModel}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">
                        Numatytasis (nemokamas → mokamas)
                      </SelectItem>
                      <SelectItem value="gemini-3.8-flash">
                        Flash 3.8
                      </SelectItem>
                      <SelectItem value="gemini-3.7-flash">
                        Flash 3.7
                      </SelectItem>
                      <SelectItem value="gemini-3.6-flash">
                        Flash 3.6
                      </SelectItem>
                      <SelectItem value="gemini-3.5-flash">
                        Flash 3.5
                      </SelectItem>
                      <SelectItem value="gemini-3.5-flash-lite">
                        Flash Lite 3.5
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Prompt layers */}
          {layersLoading ? (
            <div className="flex items-center justify-center py-12 border rounded-md">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">
                Kraunami prompt sluoksniai...
              </span>
            </div>
          ) : showComposed ? (
            <Card>
              <CardContent className="pt-4 pb-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">
                    Sujungtas prompt
                  </Label>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowComposed(false)}
                  >
                    <EyeOff className="h-3.5 w-3.5 mr-1" />
                    Slėpti
                  </Button>
                </div>
                <ComposedPromptView segments={layersData?.composed_segments || []} />
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-1">
              {layers.map((layer) => (
                <LayerAccordion
                  key={layer.key}
                  layer={layer}
                  expanded={expandedLayers.has(layer.key)}
                  onToggle={() => toggleLayer(layer.key)}
                  content={editedLayers[layer.key] ?? layer.content ?? ""}
                  onContentChange={(val) => setLayerContent(layer.key, val)}
                  modified={isLayerModified(layer)}
                  onSave={() => handleSaveLayer(layer)}
                  saving={savingSkill || savingLayer}
                />
              ))}

              {/* Show composed button */}
              {layers.some((l) => l.exists) && (
                <div className="pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowComposed(true)}
                  >
                    <Eye className="h-3.5 w-3.5 mr-2" />
                    Rodyti sujungtą prompt
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3">
            <Button
              onClick={handleGenerate}
              disabled={generating || layersLoading}
            >
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Generuojama...
                </>
              ) : (
                <>
                  <Rocket className="h-4 w-4 mr-2" />
                  Generuoti {ltPlural(parseInt(count) || 50, "pratimą", "pratimus", "pratimų")}
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Review */}
      {currentStep === "review" && (
        <div className="space-y-4">
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium">
                  Sugeneruoti pratimai ({exercises.length})
                </h3>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5 text-xs">
                    <Badge variant="outline" className="text-green-600 border-green-300">
                      {approvedCount} patvirtinta
                    </Badge>
                    {unapprovedCount > 0 && (
                      <Badge variant="outline" className="text-orange-600 border-orange-300">
                        {unapprovedCount} nepatvirtinta
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleApproveAll}
                      className="text-xs h-7 text-green-600 hover:text-green-700"
                    >
                      <CheckCircle className="h-3.5 w-3.5 mr-1" />
                      Patvirtinti visus
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleUnapproveAll}
                      className="text-xs h-7 text-muted-foreground"
                    >
                      <XCircle className="h-3.5 w-3.5 mr-1" />
                      Atmesti visus
                    </Button>
                  </div>
                </div>
              </div>
              {exercises.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nėra sugeneruotų pratimų.
                </p>
              ) : (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {exercises.map((ex, i) => (
                    <ExercisePreviewCard
                      key={ex.filePath}
                      exercise={ex}
                      index={i}
                      approved={approvedPaths.has(ex.filePath)}
                      onToggleApproval={() => toggleApproval(ex.filePath)}
                      onDelete={() => {
                        rejectExerciseFiles({ variables: { filePaths: [ex.filePath] } }).then(({ data }) => {
                          if (data?.rejectExerciseFiles?.success) {
                            toast.success("Ištrinta");
                            setExercises((prev) => prev.filter((e) => e.filePath !== ex.filePath));
                            setApprovedPaths((prev) => { const next = new Set(prev); next.delete(ex.filePath); return next; });
                          } else {
                            toast.error(data?.rejectExerciseFiles?.errors?.join(", ") || "Klaida");
                          }
                        }).catch((e: unknown) => {
                          toast.error(e instanceof Error ? e.message : "Klaida");
                        });
                      }}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => setCurrentStep("prompt")}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Grįžti prie prompt
            </Button>

            <div className="flex items-center gap-3">
              {exercises.length > 0 && (
                <>
                  <Button
                    variant="outline"
                    onClick={handleClearUnapproved}
                    disabled={deleting || unapprovedCount === 0}
                    className="text-red-600 hover:text-red-700"
                  >
                    {deleting ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-2" />
                    )}
                    Ištrinti nepatvirtintus ({unapprovedCount})
                  </Button>
                  <Button
                    onClick={handleSaveApproved}
                    disabled={approving || approvedCount === 0}
                  >
                    {approving ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Check className="h-4 w-4 mr-2" />
                    )}
                    Išsaugoti patvirtintus ({approvedCount})
                  </Button>
                </>
              )}
              <Button
                variant="outline"
                onClick={() => {
                  setExercises([]);
                  setApprovedPaths(new Set());
                  setCurrentStep("prompt");
                }}
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Generuoti dar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LayerAccordion({
  layer,
  expanded,
  onToggle,
  content,
  onContentChange,
  modified,
  onSave,
  saving,
}: {
  layer: LayerInfo;
  expanded: boolean;
  onToggle: () => void;
  content: string;
  onContentChange: (val: string) => void;
  modified: boolean;
  onSave: () => void;
  saving: boolean;
}) {
  const isEmpty = !layer.exists && !content;

  return (
    <div className="border rounded-md overflow-hidden">
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-accent/50 transition-colors"
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        {layer.exists ? (
          <FileText className="h-3.5 w-3.5 shrink-0 text-primary" />
        ) : (
          <Plus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className={`font-medium truncate ${!layer.exists ? "text-muted-foreground" : ""}`}>
          {layer.label}
        </span>
        <span className="text-xs text-muted-foreground truncate ml-1">
          {layer.filename}.md
        </span>
        <div className="ml-auto flex items-center gap-1.5 shrink-0">
          {modified && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-orange-600 border-orange-300">
              pakeistas
            </Badge>
          )}
          {!layer.exists && !modified && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground">
              tuščias
            </Badge>
          )}
        </div>
      </button>

      {/* Body */}
      {expanded && (
        <div className="border-t px-3 py-3 space-y-2 bg-muted/20">
          <Textarea
            value={content}
            onChange={(e) => onContentChange(e.target.value)}
            rows={isEmpty ? 4 : 12}
            className="font-mono text-xs"
            placeholder="Įveskite prompt fragmentą..."
          />
          <div className="flex items-center justify-between">
            <p className="text-[11px] text-muted-foreground">
              Placeholder: {"{{LEVEL}}"}, {"{{LEVEL_NAME}}"}, {"{{COUNT}}"}, {"{{HINT_INSTRUCTION}}"}
            </p>
            {modified && (
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  onSave();
                }}
                disabled={saving}
              >
                {saving ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                ) : (
                  <Save className="h-3.5 w-3.5 mr-1" />
                )}
                Išsaugoti
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ExercisePreviewCard({
  exercise,
  index,
  approved,
  onToggleApproval,
  onDelete,
}: {
  exercise: ExercisePreview;
  index: number;
  approved: boolean;
  onToggleApproval: () => void;
  onDelete?: () => void;
}) {
  return (
    <div
      className={`border rounded-lg p-3 space-y-2 transition-colors cursor-pointer ${
        approved
          ? "bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800"
          : "bg-background"
      }`}
      onClick={onToggleApproval}
    >
      <div className="flex items-center gap-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleApproval();
          }}
          className={`shrink-0 h-6 w-6 rounded-full flex items-center justify-center border-2 transition-colors ${
            approved
              ? "bg-green-500 border-green-500 text-white"
              : "border-muted-foreground/30 hover:border-green-400"
          }`}
        >
          {approved && <Check className="h-3.5 w-3.5" />}
        </button>
        <Badge variant="outline" className="text-xs shrink-0">
          {index + 1}
        </Badge>
        <span className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-foreground font-mono truncate max-w-[300px]">
            {exercise.filePath}
          </span>
          {onDelete && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="shrink-0 h-6 w-6 flex items-center justify-center rounded text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950 transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </span>
      </div>
      {exercise.problemHtml && (
        <div
          className="text-sm border rounded p-3 bg-muted/30 [&_p]:mb-1 [&_svg]:inline-block [&_svg]:max-w-full [&_img]:max-w-[140px] [&_img]:h-auto [&_img]:rounded [&_img]:align-middle"
          dangerouslySetInnerHTML={{ __html: exercise.problemHtml }}
        />
      )}
      <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
        <span>
          <strong>Atsakymas:</strong>{" "}
          <code className="bg-muted px-1 py-0.5 rounded">{exercise.answer}</code>
        </span>
        {exercise.choices && exercise.choices.length > 0 && (
          <span>
            <strong>Pasirinkimai:</strong>{" "}
            {exercise.choices.map((c, i) => (
              <code key={i} className="bg-muted px-1 py-0.5 rounded mr-1">{c}</code>
            ))}
          </span>
        )}
        {exercise.hint && (
          <span>
            <strong>Užuomina:</strong> {exercise.hint}
          </span>
        )}
      </div>
    </div>
  );
}

const SEGMENT_STYLES: Record<string, { border: string; bg: string; badge: string; badgeBg: string }> = {
  layer: {
    border: "border-l-blue-400",
    bg: "bg-blue-50/40 dark:bg-blue-950/20",
    badge: "text-blue-700 dark:text-blue-300",
    badgeBg: "bg-blue-100 dark:bg-blue-900/50",
  },
  shape_pack: {
    border: "border-l-emerald-400",
    bg: "bg-emerald-50/40 dark:bg-emerald-950/20",
    badge: "text-emerald-700 dark:text-emerald-300",
    badgeBg: "bg-emerald-100 dark:bg-emerald-900/50",
  },
  concepts: {
    border: "border-l-sky-400",
    bg: "bg-sky-50/40 dark:bg-sky-950/20",
    badge: "text-sky-700 dark:text-sky-300",
    badgeBg: "bg-sky-100 dark:bg-sky-900/50",
  },
  skill: {
    border: "border-l-violet-400",
    bg: "bg-violet-50/40 dark:bg-violet-950/20",
    badge: "text-violet-700 dark:text-violet-300",
    badgeBg: "bg-violet-100 dark:bg-violet-900/50",
  },
};

const SEGMENT_KINDS: Record<string, string> = {
  layer: "sluoksnis",
  shape_pack: "SVG",
  concepts: "sąvokos",
  skill: "įgūdis",
};

function HighlightedContent({ text }: { text: string }) {
  // Split on «...» markers that wrap resolved placeholder values
  const parts = text.split(/(«[^»]*»)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith("«") && part.endsWith("»")) {
          const value = part.slice(1, -1);
          return (
            <span
              key={i}
              className="bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200 rounded px-0.5"
            >
              {value}
            </span>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}

function ComposedPromptView({ segments }: { segments: ComposedSegment[] }) {
  if (segments.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">
        (tuščia – nėra aktyvių sluoksnių)
      </p>
    );
  }

  return (
    <div className="space-y-2 max-h-[70vh] overflow-y-auto">
      {segments.map((seg, i) => {
        const style = SEGMENT_STYLES[seg.type] || SEGMENT_STYLES.layer;
        return (
          <div
            key={i}
            className={`border-l-4 rounded-r-md ${style.border} ${style.bg}`}
          >
            {/* Non-selectable label */}
            <div
              className={`select-none px-3 py-1.5 text-[11px] font-medium tracking-wide ${style.badge} ${style.badgeBg} rounded-tr-md flex items-center gap-2`}
            >
              <span className="opacity-60 uppercase">
                {SEGMENT_KINDS[seg.type] || seg.type}
              </span>
              <span>{seg.label}</span>
            </div>
            {/* Selectable content */}
            <pre className="px-3 py-2 text-xs font-mono whitespace-pre-wrap break-words leading-relaxed">
              <HighlightedContent text={seg.content} />
            </pre>
          </div>
        );
      })}
    </div>
  );
}

function StepBadge({
  step,
  label,
  active,
  done,
  onClick,
}: {
  step: number;
  label: string;
  active: boolean;
  done: boolean;
  onClick?: () => void;
}) {
  const clickable = onClick && (done || active);
  return (
    <button
      onClick={clickable ? onClick : undefined}
      disabled={!clickable}
      className={`
        inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors
        ${active
          ? "bg-primary text-primary-foreground"
          : done
          ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 cursor-pointer hover:bg-green-200 dark:hover:bg-green-800"
          : "bg-muted text-muted-foreground"
        }
        ${!clickable ? "cursor-default" : ""}
      `}
    >
      {done ? <Check className="h-3 w-3" /> : <span>{step}</span>}
      {label}
    </button>
  );
}
